import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Fade,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import CustomButton from "./CustomButton";
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';

interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  content: string;
  severity?: 'error' | 'warning' | 'info' | 'success';
  confirmText?: string;
  cancelText?: string;
  loading?: boolean;
  customContent?: ReactNode;
}

export default function ConfirmationDialog({
  open,
  onClose,
  onConfirm,
  title,
  content,
  severity = 'warning',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  loading = false,
  customContent,
}: ConfirmationDialogProps) {
  const theme = useTheme();

  const getIcon = () => {
    switch (severity) {
      case 'error': return <ErrorOutlineRoundedIcon sx={{ fontSize: 40, color: theme.palette.error.main }} />;
      case 'success': return <CheckCircleOutlineRoundedIcon sx={{ fontSize: 40, color: theme.palette.success.main }} />;
      case 'info': return <InfoOutlinedIcon sx={{ fontSize: 40, color: theme.palette.info.main }} />;
      case 'warning':
      default: return <WarningAmberRoundedIcon sx={{ fontSize: 40, color: theme.palette.warning.main }} />;
    }
  };

  const getBgColor = () => {
    switch (severity) {
      case 'error': return theme.palette.error.light;
      case 'success': return theme.palette.success.light;
      case 'info': return theme.palette.info.light;
      case 'warning':
      default: return theme.palette.warning.light;
    }
  };

  const formattedBgColor = alpha(getBgColor(), 0.1); // 10% opacity for better contrast

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          padding: 2,
          minWidth: 320,
          maxWidth: 400,
          textAlign: 'center',
        },
      }}
    >
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pb: 1, pt: 3 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: formattedBgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          {getIcon()}
        </Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {content}
        </Typography>
        {customContent ? <Box sx={{ width: '100%', mt: 1 }}>{customContent}</Box> : null}
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center', pb: 2, flexDirection: 'column', gap: 1 }}>
        <CustomButton
          onClick={onConfirm}
          variant="primary"
          color={severity === 'error' ? 'error' : 'primary'}
          disabled={loading}
          fullWidth
        >
          {confirmText}
        </CustomButton>
        <CustomButton
          onClick={onClose}
          disabled={loading}
          variant="secondary"
          fullWidth
        >
          {cancelText}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
}
